import { useMemo, useState } from "react";

import { UserCard } from "@/components/karma/UserCard";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ensureStore,
  getMe,
  getUserById,
  listLikesGiven,
  listLikesReceived,
  toggleLike,
} from "@/lib/karma/storage";

export default function Likes() {
  const [storeVersion, setStoreVersion] = useState(0);

  const store = useMemo(() => {
    storeVersion;
    return ensureStore();
  }, [storeVersion]);

  const me = getMe(store);

  const likedBy = useMemo(() => {
    const blocked = new Set(me.blockedUserIds ?? []);
    const passed = new Set(me.passedUserIds ?? []);

    const received = listLikesReceived(me.id, store);
    const uniqueFrom = Array.from(new Set(received.map((l) => l.fromUserId)));

    return uniqueFrom
      .map((id) => getUserById(id, store))
      .filter(Boolean)
      .filter((u) => !blocked.has(u!.id))
      .filter((u) => !passed.has(u!.id))
      .filter((u) => !(u!.blockedUserIds ?? []).includes(me.id))
      .slice(0, 50);
  }, [me.id, me.blockedUserIds, me.passedUserIds, store, storeVersion]);

  const iLiked = useMemo(() => {
    const blocked = new Set(me.blockedUserIds ?? []);
    const passed = new Set(me.passedUserIds ?? []);

    const given = listLikesGiven(me.id, store);
    const uniqueTo = Array.from(new Set(given.map((l) => l.toUserId)));

    return uniqueTo
      .map((id) => getUserById(id, store))
      .filter(Boolean)
      .filter((u) => !blocked.has(u!.id))
      .filter((u) => !passed.has(u!.id))
      .filter((u) => !(u!.blockedUserIds ?? []).includes(me.id))
      .slice(0, 50);
  }, [me.id, me.blockedUserIds, me.passedUserIds, store, storeVersion]);

  return (
    <div className="w-full space-y-4">
      <div className="space-y-1">
        <h1 className="font-display text-2xl font-semibold tracking-tight">Beğeniler</h1>
        <p className="text-sm text-muted-foreground">
          Seni beğenenler ve senin beğendiklerin burada listelenir.
        </p>
      </div>

      <Tabs defaultValue="received">
        <TabsList className="w-full">
          <TabsTrigger value="received" className="flex-1">
            Seni beğenenler ({likedBy.length})
          </TabsTrigger>
          <TabsTrigger value="given" className="flex-1">
            Senin beğendiklerin ({iLiked.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="received" className="mt-4">
          {likedBy.length === 0 ? (
            <Card className="p-5 text-sm text-muted-foreground">Henüz seni beğenen yok.</Card>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {likedBy.map((u) => (
                <UserCard
                  key={u!.id}
                  user={u!}
                  showMatchBadge
                  onLike={() => {
                    toggleLike(u!.id);
                    setStoreVersion((v) => v + 1);
                  }}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="given" className="mt-4">
          {iLiked.length === 0 ? (
            <Card className="p-5 text-sm text-muted-foreground">Henüz kimseyi beğenmedin.</Card>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {iLiked.map((u) => (
                <UserCard
                  key={u!.id}
                  user={u!}
                  showMatchBadge
                  onLike={() => {
                    toggleLike(u!.id);
                    setStoreVersion((v) => v + 1);
                  }}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
